import { MemberProfile } from "../../model/members/MemberProfile";

import { useState } from "react";
import { AutoComplete, Button, Checkbox, notification, Space } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useMemberService } from "../../services/members/MemberServiceProvider";
import { useClient } from "../../common/ClientProvider";
import { MemberOptionLabel, useMemberSearch } from "./memberSearch";

// Persists the "Active only" filter across reloads and screens. Namespaced per
// tenant so a preference set in one club doesn't leak into another sharing the
// same browser. NB: the E2E tests reference this key literal — keep them in sync.
const ACTIVE_ONLY_STORAGE_PREFIX = 'memberSearchActiveOnly';
const activeOnlyStorageKey = (clientId: string) => `${ACTIVE_ONLY_STORAGE_PREFIX}:${clientId}`;

interface MemberSearchBarProps {
    onSelect: (member: MemberProfile) => void;
    onRefresh: () => void;
    onMemberSelect?: (member: MemberProfile) => void;
}

const MemberSearchBar: React.FC<MemberSearchBarProps> = ({ onSelect, onRefresh, onMemberSelect }) => {
    const memberService = useMemberService();
    const { currentClientId } = useClient();
    const storageKey = activeOnlyStorageKey(currentClientId);
    // Default to active members only; unticking widens the search to everyone.
    // Persist the choice so it sticks across reloads and every screen embedding
    // this bar (POS, member screens, joint membership, guest signup).
    const [activeOnly, setActiveOnly] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        return saved === null ? true : saved === 'true';
    });
    const { results, search, reset } = useMemberSearch(activeOnly);
    const [searchValue, setSearchValue] = useState('');
    const [selectedMembershipId, setSelectedMembershipId] = useState<string>();

    // Results arrive ranked from the backend; `defaultActiveFirstOption` highlights
    // results[0] so pressing Enter selects the single/top match (or the user's
    // arrow-key choice when they navigate the list).
    const options = results.map(member => ({
        value: member.membershipId,
        label: <MemberOptionLabel member={member} />,
    }));

    const handleSearch = (value: string) => {
        setSearchValue(value);
        search(value);
    };

    const handleChange = (value: string) => {
        setSearchValue(value);
        if (!value) reset();
    };

    const handleActiveOnlyChange = (e: CheckboxChangeEvent) => {
        setActiveOnly(e.target.checked);
        localStorage.setItem(storageKey, String(e.target.checked));
    };

    // A search hit is slim; fetch the full profile so every downstream consumer
    // (POS, member screens, joint membership, guest signup) still gets a full
    // MemberProfile as before.
    const loadFullProfile = async (membershipId: string) => {
        try {
            const profile = await memberService.getMember(membershipId);
            try {
                profile.photoUrl = await memberService.getMemberPhoto(membershipId);
            } catch {
                // Photo is best-effort; a missing image must not block selection.
            }
            const hit = results.find(r => r.membershipId === membershipId);
            setSelectedMembershipId(membershipId);
            setSearchValue(hit?.name ?? profile.name);
            reset();

            if (onMemberSelect) {
                onMemberSelect(profile);
            }
            onSelect(profile);
        } catch (error) {
            notification.error({ message: 'Error', description: 'Failed to load member' });
        }
    };

    const handleRefresh = async () => {
        // Re-pull the currently selected member's data, then let the parent refresh.
        if (selectedMembershipId) {
            await loadFullProfile(selectedMembershipId);
        }
        onRefresh();
    };

    return (
        <Space>
            <AutoComplete
                style={{ width: 320 }}
                options={options}
                onSearch={handleSearch}
                onSelect={(value: string) => loadFullProfile(value)}
                onChange={handleChange}
                value={searchValue}
                defaultActiveFirstOption
                placeholder="Search by name, membership number, legacy ID, phone, or email"
            />
            <Checkbox
                checked={activeOnly}
                onChange={handleActiveOnlyChange}
                data-testid="member-search-active-only"
            >
                Active only
            </Checkbox>
            <Button onClick={handleRefresh}>Refresh</Button>
        </Space>
    );
}

export default MemberSearchBar;
